import {
  Cart,
  CustomLineItem,
  CustomLineItemDraft,
  LocalizedString,
} from "@commercetools/platform-sdk";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { updateCartAddOn } from "@/app/actions";
import { config } from "@/config";
import { CartAddOnUpdate } from "@/features/basket/types";
import { useCart } from "./use-cart";

type ArtworkCheckAddOn = {
  slug: string;
  name: LocalizedString;
  centAmount: number;
  lineItemKey: string;
};

const artworkCheck = {
  slug: "artwork-check",
  name: {
    en: "Artwork Check",
  },
  centAmount: 1000,
};

function getArtworkCheckAddon(addOn: ArtworkCheckAddOn): CustomLineItemDraft {
  return {
    name: addOn.name,
    quantity: 1,
    money: {
      type: "centPrecision",
      centAmount: addOn.centAmount,
      currencyCode: "GBP",
      fractionDigits: 2,
    },
    slug: addOn.slug,
    priceMode: "Standard",
    taxCategory: {
      typeId: "tax-category",
      id: config.NEXT_PUBLIC_STANDARD_TAX_CATEGORY_ID,
    },
    custom: {
      type: { typeId: "type", key: "custom-line-item-additional-fields" },
      fields: {
        lineItemKey: addOn.lineItemKey,
      },
    },
  };
}

export function getUpdateArtworkCheckPricePayload(
  cart: Cart | null | undefined,
  lineItemKey: string,
  price: number,
): CartAddOnUpdate | undefined {
  if (cart == null) return;

  const artworkCheckAddOn = cart.customLineItems.find(
    (item) =>
      item.slug.includes("artwork-check") && item.custom?.fields.lineItemKey === lineItemKey,
  );

  if (!artworkCheckAddOn) return;

  const customLineItemArtworkCheck: CustomLineItemDraft = getArtworkCheckAddon({
    slug: `artwork-check-${+new Date()}`,
    name: {
      en: artworkCheck.name.en,
    },
    centAmount: price,
    lineItemKey,
  });

  const payload: CartAddOnUpdate = {
    action: "changeCustomLineItemMoney",
    cartId: cart.id,
    cartVersion: cart?.version!,
    lineId: artworkCheckAddOn!.id,
    customLineItemDraft: customLineItemArtworkCheck,
  };

  return payload;
}

export function getUpdateArtworkCheckActionPayload(
  cart: Cart | null | undefined,
  hasAddOn: boolean,
  lineItemKey: string,
  price: number,
) {
  if (cart == null) return null;

  const customLineItemArtworkCheck: CustomLineItemDraft = getArtworkCheckAddon({
    slug: `artwork-check-${+new Date()}`,
    name: {
      en: artworkCheck.name.en,
    },
    centAmount: price,
    lineItemKey,
  });

  const artworkCheckAddOn = cart.customLineItems.find(
    (item) =>
      item.slug.includes("artwork-check") && item.custom?.fields.lineItemKey === lineItemKey,
  );

  if (!hasAddOn && !artworkCheckAddOn) return;

  const payload: CartAddOnUpdate = hasAddOn
    ? {
        action: "addCustomLineItem",
        cartId: cart.id,
        cartVersion: cart?.version!,
        customLineItemDraft: customLineItemArtworkCheck,
      }
    : {
        action: "removeCustomLineItem",
        cartId: cart.id,
        cartVersion: cart.version,
        lineId: artworkCheckAddOn!.id,
      };

  return payload;
}

export async function updateArtworkCheckPriceAddOn(
  cart: Cart | null | undefined,
  lineItemKey: string,
  price: number,
) {
  const payload = getUpdateArtworkCheckPricePayload(cart, lineItemKey, price);
  if (!payload) return;

  const res = await updateCartAddOn(payload);

  return res;
}

export function useArtworkCheckPrice() {
  const queryClient = useQueryClient();

  const { data: cart } = useCart();

  return useMutation({
    mutationFn: ({ lineItemKey, price }: { lineItemKey: string; price: number }) =>
      updateArtworkCheckPriceAddOn(cart, lineItemKey, price),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["CART"] });
    },
    onError: (err: Error) => {
      toast.error(`An error occurred ${err.message}`);
    },
  });
}

export async function updateArtworkCheckAddOn(
  cart: Cart | null | undefined,
  hasAddOn: boolean,
  lineItemKey: string,
  price: number,
) {
  const payload = getUpdateArtworkCheckActionPayload(cart, hasAddOn, lineItemKey, price);
  if (!payload) return;

  const res = await updateCartAddOn(payload);

  return res;
}

export function useSetArtworkCheckAddOn() {
  const queryClient = useQueryClient();
  const { data: cart } = useCart();

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: ({
      hasAddOn,
      lineItemKey,
      price,
    }: {
      hasAddOn: boolean;
      lineItemKey: string;
      price: number;
    }) => updateArtworkCheckAddOn(cart, hasAddOn, lineItemKey, price),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["CART"] });
      return cart;
    },
    onError: (data: Error) => {
      toast.error("Error updating addon in cart: " + data.message);
    },
  });

  return { mutateAsync, isLoading };
}

export const UseArtworkCheck = (
  customLineItems: CustomLineItem[] | undefined,
  lineItemKey: string,
) => {
  if (!customLineItems?.length) return undefined;

  return customLineItems.find(
    (item) =>
      item.slug.includes("artwork-check") && item.custom?.fields.lineItemKey === lineItemKey,
  );
};

export const UseArtworkChecks = (customLineItems: CustomLineItem[] | undefined) => {
  if (!customLineItems?.length) return undefined;

  return customLineItems.filter((item) => item.slug.includes("artwork-check"));
};
